import { Fragment, MouseEvent } from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { Badge, Button, Flex, Icon, OverflowText, Text, Tooltip } from '@servicetitan/anvil2';
import IconArrowBackIos from '@servicetitan/anvil2/assets/icons/material/round/arrow_back_ios.svg';
import IconClose from '@servicetitan/anvil2/assets/icons/material/round/close.svg';
import IconDragIndicator from '@servicetitan/anvil2/assets/icons/material/round/drag_indicator.svg';
import IconInbox from '@servicetitan/anvil2/assets/icons/material/round/inbox.svg';
import IconModeEdit from '@servicetitan/anvil2/assets/icons/material/round/mode_edit.svg';
import IconOpenInFull from '@servicetitan/anvil2/assets/icons/material/round/open_in_full.svg';

import * as styles from './header.module.less';

export interface HeaderProps {
    title?: string;
    onMouseDown?: (e: MouseEvent) => void;
    isDragging?: boolean;
    isDraggable?: boolean;
    isExpanded?: boolean;
    disabledChatsIcon?: boolean;
    historyCount?: number;
    titleBadge?: string | number;
    className?: string;
    onBack?: () => void;
    onClose?: () => void;
    onViewHistory?: () => void;
    onFullscreen?: () => void;
    onCreateNewChat?: () => void;
    backButtonId?: string;
    viewHistoryButtonId?: string;
    fullscreenButtonId?: string;
    newChatButtonId?: string;
    closeButtonId?: string;
}

export const Header = observer(
    ({
        title = 'Atlas',
        onMouseDown,
        isDraggable,
        isDragging,
        onFullscreen,
        onViewHistory,
        isExpanded,
        historyCount,
        titleBadge,
        disabledChatsIcon,
        onClose,
        onBack,
        className = '',
        onCreateNewChat,
        backButtonId,
        viewHistoryButtonId,
        fullscreenButtonId,
        newChatButtonId,
        closeButtonId,
    }: HeaderProps) => {
        return (
            <Flex className={classNames(styles.chatHeader, className)}>
                <Flex gap={1} className="m-inline-end-1">
                    {isDraggable && !isExpanded && (
                        <Flex
                            alignSelf="center"
                            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                            onMouseDown={onMouseDown ?? undefined}
                            className="cursor-grab"
                        >
                            <Tooltip placement="bottom">
                                <Tooltip.Trigger>
                                    <Icon svg={IconDragIndicator} size="medium" inherit />
                                </Tooltip.Trigger>
                                {isDragging ? null : (
                                    <Tooltip.Content>Drag to Adjust</Tooltip.Content>
                                )}
                            </Tooltip>
                        </Flex>
                    )}
                    {onBack && (
                        <Button
                            id={backButtonId}
                            appearance="ghost"
                            size="small"
                            icon={IconArrowBackIos}
                            onClick={onBack}
                        />
                    )}
                </Flex>
                <Flex grow={1}>
                    <Tooltip placement="bottom">
                        <Tooltip.Trigger>
                            <OverflowText rows={1} className={styles.chatHeaderTitle}>
                                <Text
                                    el="h4"
                                    size="small"
                                    variant="headline"
                                    className="m-block-end-0 p-block-end-0"
                                >
                                    {title}
                                </Text>
                                {!!titleBadge && (
                                    <Badge
                                        offset={{
                                            x: '12px',
                                            y: '0.7rem',
                                        }}
                                    >
                                        {titleBadge}
                                    </Badge>
                                )}
                            </OverflowText>
                        </Tooltip.Trigger>
                        <Tooltip.Content>{title}</Tooltip.Content>
                    </Tooltip>
                </Flex>
                <Flex gap={1}>
                    {onViewHistory && (
                        <Tooltip placement="bottom">
                            <Tooltip.Trigger style={{ position: 'relative' }}>
                                <Fragment>
                                    <Button
                                        id={viewHistoryButtonId}
                                        style={{ position: 'relative' }}
                                        disabled={disabledChatsIcon}
                                        appearance="ghost"
                                        size="small"
                                        icon={IconInbox}
                                        aria-label="chats"
                                        onClick={onViewHistory}
                                    />
                                    {!!historyCount && <Badge>{historyCount}</Badge>}
                                </Fragment>
                            </Tooltip.Trigger>
                            <Tooltip.Content>View All Chats</Tooltip.Content>
                        </Tooltip>
                    )}
                    {onFullscreen && (
                        <Tooltip placement="bottom">
                            <Tooltip.Trigger>
                                <Button
                                    id={fullscreenButtonId}
                                    appearance="ghost"
                                    size="small"
                                    icon={IconOpenInFull}
                                    aria-label="fullscreen"
                                    onClick={onFullscreen}
                                />
                            </Tooltip.Trigger>
                            <Tooltip.Content>
                                {isExpanded ? 'Minimize Window' : 'Expand Window'}
                            </Tooltip.Content>
                        </Tooltip>
                    )}
                    {onCreateNewChat && (
                        <Tooltip placement="bottom">
                            <Tooltip.Trigger>
                                <Button
                                    id={newChatButtonId}
                                    size="small"
                                    icon={IconModeEdit}
                                    aria-label="new chat"
                                    onClick={onCreateNewChat}
                                >
                                    New Chat
                                </Button>
                            </Tooltip.Trigger>
                            <Tooltip.Content>New Chat</Tooltip.Content>
                        </Tooltip>
                    )}
                    {onClose && (
                        <Tooltip placement="bottom">
                            <Tooltip.Trigger>
                                <Button
                                    id={closeButtonId}
                                    appearance="ghost"
                                    size="small"
                                    icon={IconClose}
                                    aria-label="close"
                                    onClick={onClose}
                                />
                            </Tooltip.Trigger>
                            <Tooltip.Content>Hide Window</Tooltip.Content>
                        </Tooltip>
                    )}
                </Flex>
            </Flex>
        );
    }
);
